// Component: TradeCard.jsx
// Purpose: Individual trade card with accordion — collapsed summary + expandable order table; trade_set_type: algo_bracket | degenerate (R105.3)

import React, { useState, useRef, useEffect, useMemo } from 'react';
import SLTPProgressBar from './SLTPProgressBar';
import { Badge } from '../ds';
import { LEG_TYPE_DISPLAY, _fmtTimeIST, _fmtPrice, formatExitConversion, resolveProductLabel, getOrderWorkingPrice, resolveCurrentBracketLevels, pickLevelPair, describeOrderCharges, chargeBreakdownText, chargeReasonPhrase } from '../../utils/tradingTransforms';
//* Shared reject-reason map — single source of truth; also used by ManualOrdersPanel + SmartOrderForm.
import { rejectReasonLabel, fullRejectReason } from '../../utils/rejectReason';

//--> Exit reason → DS Badge variant map.
//*   Flip Exit → 'warn' (amber) intentionally stays DISTINCT from SL Hit → 'loss' (red):
//*   Flip Exit signals a strategy-design gap; SL Hit is a loss-class event. QA-critical distinction.
const EXIT_VARIANT = {
  'TP Hit':      'profit',
  'SL Hit':      'loss',
  'Signal Exit': 'info',
  'Time Exit':   'info',
  'Rollover':    'warn',
  'Flip Exit':   'warn',
  'Closed':      'neutral',
  //--> BD FIFO-pairing (Phase 1): an INTRADAY remainder anchored against a
  //*   matching DELIVERY-class broker position (same-day MIS→CNC-style
  //*   conversion). Distinct 'info' — not a loss/win event, just a product-type
  //*   change; reuses the existing Signal Exit/Time Exit variant.
  'Converted to delivery': 'info',
};

//--> Inline label style (small uppercase above value) — DS eyebrow scale
const labelStyle = {
  color: 'var(--fg-3)', fontSize: 10, textTransform: 'uppercase',
  letterSpacing: '0.6px', marginBottom: 2, fontWeight: 600,
};

//--> Shared muted meta text (times / duration / qty)
const metaStyle = { color: 'var(--fg-3)', fontSize: 11 };

//--> 144-6 R7-T4 — format an API figure for display (no arithmetic: the OMS supplies net, gross and charges).
const fmtSigned = (n) => (n >= 0 ? '+' : '') + n.toFixed(2);

//--> D-G3-2 — label of the API's gross_source on a broker-direct pair head (text only).
const GROSS_SOURCE_LABELS = { broker_reported: 'broker-reported', order_view_pairing: 'paired from fills' };

//! Closed-trade headline: ONLY the lifecycle head renders the API lifecycle net_pnl, with a
//  "Gross X / Charges Y" line; any unresolved leg reads "Unresolved" (never a number, never 0).
function ClosedNetHeadline({ lifecycleNet }) {
  if (lifecycleNet.net != null) {
    const color = lifecycleNet.net > 0 ? 'var(--color-profit)' : lifecycleNet.net < 0 ? 'var(--color-loss)' : 'var(--fg-2)';
    return (
      <div style={{ textAlign: 'right', whiteSpace: 'nowrap' }}>
        <div data-testid="trade-net-headline" style={{ color, fontWeight: 700, fontSize: 15 }}>
          {fmtSigned(lifecycleNet.net)}
        </div>
        {lifecycleNet.gross != null && lifecycleNet.charges != null && (
          <div data-testid="trade-gross-and-charges" style={{ color: 'var(--fg-3)', fontSize: 10 }}>
            Gross {lifecycleNet.gross.toFixed(2)}{GROSS_SOURCE_LABELS[lifecycleNet.grossSource] ? ` (${GROSS_SOURCE_LABELS[lifecycleNet.grossSource]})` : ''} / Charges {lifecycleNet.charges.toFixed(2)}
            {lifecycleNet.indicative ? ' · Indicative' : ''}
          </div>
        )}
      </div>
    );
  }
  const phrase = chargeReasonPhrase(lifecycleNet.unavailableReason);
  const grossLabel = GROSS_SOURCE_LABELS[lifecycleNet.grossSource];
  return (
    <div style={{ textAlign: 'right', whiteSpace: 'nowrap' }}>
      <div
        data-testid="trade-net-headline"
        title={lifecycleNet.unavailableReason || undefined}
        style={{ color: 'var(--fg-2)', fontWeight: 700, fontSize: 15 }}
      >
        {phrase ? `Unresolved (${phrase})` : 'Unresolved'}
      </div>
      {lifecycleNet.gross != null && (
        <div data-testid="trade-gross-only" style={{ color: 'var(--fg-3)', fontSize: 10 }}>
          Gross {lifecycleNet.gross.toFixed(2)}{grossLabel ? ` · ${grossLabel}` : ''}
        </div>
      )}
    </div>
  );
}

//--> Per-order charges cell for the expanded leg table: a number (breakdown in the tooltip) or an explicit reason.
function OrderChargesCell({ order }) {
  const d = describeOrderCharges(order);
  if (d.state === 'none') return <span>—</span>;
  if (d.state === 'unavailable') {
    return <span title={d.reason} style={{ color: 'var(--fg-3)', fontStyle: 'italic' }}>Unavailable{d.reasonPhrase ? ` (${d.reasonPhrase})` : ''}</span>;
  }
  return (
    <span title={chargeBreakdownText(order) || undefined}>
      {d.total.toFixed(2)}
      {d.indicative && <span style={{ color: 'var(--fg-3)', fontSize: 10, marginLeft: 4 }}>Indicative</span>}
    </span>
  );
}

/**
 * TradeCard
 * @param {Object}  trade             - TradeGroup or fill-event object
 * @param {number}  ltp               - Live last-traded price (for open ALGO_BRACKET trades)
 * @param {Object}  position          - Open position snapshot (for live P&L)
 * @param {boolean} defaultExpanded   - Expand order table on mount
 * @param {string}  trade_set_type    - 'algo_bracket' | 'degenerate' (R105.3)
 *                                      degenerate: render only fill event row, NO SL/TP placeholders
 *                                      algo_bracket: entry + SL + TP + exit (existing behavior)
 * @param {string}  source_tag        - source_tag for broker-direct label in degenerate mode
 */
export default function TradeCard({ trade, ltp, position, defaultExpanded = false, trade_set_type, source_tag }) {
  //! R105.3 — degenerate mode: broker-direct fills or fills without algo bracket context
  const isDegenerate = trade_set_type === 'degenerate';
  //! trades-view-strategy-backfill (issue #3) — read the EXPLICIT trade.isBrokerDirect
  //  flag that tradingTransforms.js's BD group builders (_buildBdPairedGroups,
  //  §4 bdDegenerateGroups) already set, instead of inferring BD-ness from
  //  "no entryOrder + no bracketOrders". That shape-based inference was never
  //  actually exercised by real BD data (BD groups always set entryOrder — see
  //  bdDegenerateGroups/§4b — so it only ever matched the entryOrder truthiness
  //  check), and it silently mislabels buildOpenPositionFallbackTrade's ALGO
  //  position-only card (entryOrder:null, bracketOrders:[] by construction,
  //  NOT broker-direct) as "Broker-direct".
  const isBrokerDirect = trade.isBrokerDirect === true || source_tag === 'EXTERNAL';
  const [expanded, setExpanded] = useState(defaultExpanded);
  const contentRef = useRef(null);
  const [contentHeight, setContentHeight] = useState(0);

  useEffect(() => {
    if (contentRef.current) setContentHeight(contentRef.current.scrollHeight);
  }, [expanded, trade]);

  const isOpen = trade.status === 'open';
  //! read-lane-correctness R5.1 (F6) — a DELIVERY holdings-sale group carries the honest literal
  //!  side:'SELL' (never a fabricated open-short 'SHORT'). Render 'SELL' NEUTRAL (--fg-2, the same
  //!  neutral token pnlColor falls to): otherwise any non-LONG side renders loss-red bold, misleadingly
  //!  implying a losing trade on a sale whose pnl is honestly '—'. Existing 'LONG'/'SHORT' rendering is
  //!  byte-unchanged; only the NEW 'SELL' literal is neutral.
  //! sideTone is the SAME arm as sideColor, surfaced as a JSDOM-observable data hook: jsdom's
  //!  cssstyle strips `var(...)` from inline color, so a test cannot read the rendered token —
  //!  data-side-tone lets the R5.1 (F6) neutral-vs-loss arm be asserted non-vacuously. Colors below
  //!  are byte-unchanged; sideTone just names the branch (profit | neutral | loss).
  const sideTone = trade.side === 'LONG' ? 'profit'
    : trade.side === 'SELL' ? 'neutral'
    : 'loss';
  const sideColor = sideTone === 'profit' ? 'var(--color-profit)'
    : sideTone === 'neutral' ? 'var(--fg-2)'
    : 'var(--color-loss)';

  //--> Compute live P&L for open trades (contractMultiplier from trade group, sourced from entry order)
  const livePnl = isOpen && ltp
    ? (trade.side === 'LONG'
        ? (ltp - trade.entryPrice) * trade.qty * (trade.contractMultiplier || 1)
        : (trade.entryPrice - ltp) * trade.qty * (trade.contractMultiplier || 1))
    : trade.pnl;

  const pnlColor = livePnl > 0 ? 'var(--color-profit)' : livePnl < 0 ? 'var(--color-loss)' : 'var(--fg-2)';

  //--> 144-8b — never-filled entry outcomes belong in Row 2's status badge; Row 1 keeps the
  //*   established no-P&L dash. The transform remains the sole classification/label authority.
  const entryOutcome = trade.lifecycleNet?.unavailableReason === 'rejected_entry'
    ? `Rejected${trade.lifecycleNet.rejectReason ? ` · ${trade.lifecycleNet.rejectReason}` : ''}`
    : trade.lifecycleNet?.unavailableReason === 'cancelled_entry' ? 'Cancelled' : null;

  //--> #10472 (R7) — SL/TP levels read from the CURRENT bracket leg order rows (the market's
  //*   actual protective prices — same authority the leg table below renders), never the entry
  //*   order's construction-time copy. Fall back to the entry-row copy ONLY when no bracket leg
  //*   rows exist at all (paper/internal-watch trades that never got leg orders).
  //*   QA F-INV-5: the fallback keys off RESOLVED levels (null when every leg row is
  //*   CANCELLED/REJECTED), not off row-array presence, so an all-superseded leg set still
  //*   shows the entry copy rather than no bar at all.
  //*   Gating is PAIR-level via pickLevelPair (shared with SignalEvaluationPanel): a live leg
  //*   level is never mixed with the entry copy for the other side (QA F-DF-4/F-DF-5).
  const { slLevel, tpLevel } = useMemo(() => {
    const entryCopy = (v) => (v ? parseFloat(v) : null);
    const pair = pickLevelPair(
      resolveCurrentBracketLevels(trade.bracketOrders),
      { sl: entryCopy(trade.entryOrder?.stop_loss), tp: entryCopy(trade.entryOrder?.take_profit) },
    );
    return { slLevel: pair.sl, tpLevel: pair.tp };
  }, [trade.bracketOrders, trade.entryOrder]);

  //--> All orders for expanded table
  //*   R105.3: degenerate mode — only the fill event row; NO SL/TP/bracket placeholders
  //*   algo_bracket mode — entry + brackets, sorted by time (existing behavior)
  const allOrders = isDegenerate
    ? (trade.entryOrder ? [trade.entryOrder] : (trade.fills ?? []))
    : [trade.entryOrder, ...(trade.bracketOrders ?? [])]
        .filter(Boolean)
        .sort((a, b) => (a._epoch || 0) - (b._epoch || 0));

  //--> Instrument identity — symbol + exchange chip, inlined into the first data
  //    row so a closed/degenerate trade reads as one concise line (was a whole
  //    extra row). Sourced from lifted top-level fields; never the UUID.
  const instrumentTag = (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
      <span style={{ color: 'var(--fg-1)', fontWeight: 700, fontSize: 13 }}>
        {/* instrument-id-display-remediation — display_symbol-first; never a uuid. */}
        {trade.display_symbol ?? trade.symbol ?? trade.name ?? '—'}
      </span>
      {trade.exchange && (
        <span style={{
          background: 'var(--bg-3)', color: 'var(--fg-3)', padding: '1px 6px',
          borderRadius: 4, fontSize: 10, fontWeight: 500, letterSpacing: '0.3px',
        }}>{trade.exchange}</span>
      )}
    </span>
  );

  return (
    <div style={{
      border: '1px solid var(--divider)', borderRadius: 10,
      //--> Green left accent marks a LIVE / open trade (retained functionality).
      borderLeft: isOpen ? '3px solid var(--success)' : '1px solid var(--divider)',
      background: 'var(--bg-2)',
      fontFamily: "'Inter', system-ui, sans-serif", fontSize: 13,
      overflow: 'hidden',
    }}>
      {/* ---- Collapsed Header ---- */}
      {/*! M3: keyboard-accessible (role=button + tabIndex + Enter/Space onKeyDown + focus-visible outline) */}
      <div
        role="button"
        tabIndex={0}
        aria-expanded={expanded}
        aria-label={`Trade ${trade.side} ${trade.status === 'open' ? 'live' : trade.status}; ${expanded ? 'collapse' : 'expand'} details`}
        style={{ padding: '11px 14px', cursor: 'pointer', outline: 'none' }}
        onClick={() => setExpanded(!expanded)}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            setExpanded(!expanded);
          }
        }}
        onFocus={(e) => { if (e.currentTarget.matches(':focus-visible')) e.currentTarget.style.boxShadow = 'inset 0 0 0 2px color-mix(in oklch, var(--color-accent) 40%, transparent)'; }}
        onBlur={(e) => { e.currentTarget.style.boxShadow = 'none'; }}
      >
        {/* R105.3 — DEGENERATE mode: only fill event row rendered; no SL/TP/bracket placeholders */}
        {isDegenerate ? (
          <>
            {/* Row 1: instrument + source + side + fill price + P&L */}
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 10, marginBottom: 6 }}>
              <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap', minWidth: 0 }}>
                {instrumentTag}
                {/* broker_only trades — show "Broker-direct" source label */}
                {isBrokerDirect && <Badge variant="warn" size="sm">Broker-direct</Badge>}
                <span data-side-tone={sideTone} style={{ color: sideColor, fontWeight: 700, fontSize: 13 }}>{trade.side ?? '—'}</span>
                {trade.entryPrice != null && (
                  <span style={{ color: 'var(--fg-1)', fontSize: 13 }}>{_fmtPrice(trade.entryPrice)}</span>
                )}
              </div>
              <span style={{ color: pnlColor, fontWeight: 700, fontSize: 15, whiteSpace: 'nowrap' }}>
                {trade.pnl != null ? (trade.pnl >= 0 ? '+' : '') + trade.pnl.toFixed(2) : '—'}
              </span>
            </div>
            {/* Row 2: time + qty + expand */}
            <div style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
              <span style={metaStyle}>
                {trade.entryTime ? _fmtTimeIST(new Date(trade.entryTime).toISOString()) : '—'}
              </span>
              <span style={metaStyle}>Qty {trade.qty ?? '—'}</span>
              <span style={{ marginLeft: 'auto', color: expanded ? 'var(--color-accent)' : 'var(--fg-3)', fontSize: 11 }}>
                {expanded ? '▲ Hide orders' : '▼ View orders'}
              </span>
            </div>
          </>
        ) : isOpen ? (
          /* ---- OPEN TRADE HEADER ---- */
          <>
            {/* Row 1: instrument + LIVE + badges + side + live P&L */}
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 10, marginBottom: 8 }}>
              <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap', minWidth: 0 }}>
                {instrumentTag}
                <span style={{
                  background: 'var(--success)', color: '#fff', padding: '2px 8px',
                  borderRadius: 5, fontSize: 10, fontWeight: 600, letterSpacing: '0.5px',
                  animation: 'tradePulse 2s infinite',
                  //* #fff kept intentionally: white text on --success (green) for contrast.
                }}>LIVE</span>
                {/* BD FIFO-pairing (Phase 1): a paired BD open remainder still carries the
                    Broker-direct provenance badge (previously only shown in degenerate mode). */}
                {isBrokerDirect && <Badge variant="warn" size="sm">Broker-direct</Badge>}
                {/* Delivery/Intraday — sourced from the entry leg's product_type so the
                    operator can tell a same-session intraday trade from a multi-day
                    delivery/BTST hold. resolveProductLabel maps broker + canonical vocab. */}
                {(() => {
                  const prodLabel = resolveProductLabel(
                    trade.entryOrder?.product_type ?? trade.bracketOrders?.[0]?.product_type
                  );
                  return prodLabel ? <Badge variant="info" size="sm">{prodLabel}</Badge> : null;
                })()}
                {/* R4(c) — no clean anchor against a broker-truth position; never guess a pairing. */}
                {trade.needsReconcile && <Badge variant="warn" size="sm">Needs reconcile</Badge>}
                <span style={{ color: sideColor, fontWeight: 700, fontSize: 13 }}>{trade.side}</span>
              </div>
              <span style={{ color: pnlColor, fontWeight: 700, fontSize: 16, whiteSpace: 'nowrap' }}>
                {livePnl !== null ? (livePnl >= 0 ? '+' : '') + livePnl.toFixed(2) : '—'}
              </span>
            </div>

            {/* Row 2: Labeled fields */}
            <div style={{ display: 'flex', gap: 20, marginBottom: 10 }}>
              <div>
                <div style={labelStyle}>Entry</div>
                <div style={{ color: 'var(--fg-1)', fontSize: 13, fontWeight: 500 }}>{_fmtPrice(trade.entryPrice)}</div>
              </div>
              <div>
                <div style={labelStyle}>Qty</div>
                <div style={{ color: 'var(--fg-1)', fontSize: 13, fontWeight: 500 }}>{trade.qty}</div>
              </div>
              <div>
                <div style={labelStyle}>LTP</div>
                <div style={{ color: 'var(--color-accent)', fontSize: 13, fontWeight: 500 }}>{_fmtPrice(ltp)}</div>
              </div>
              {/* Duration intentionally omitted here: it is a CLOSED-trade-only field (see
                  the CLOSED header below). An open trade's entryTime can be days old (an
                  anchored BD remainder), so a ticking elapsed-time value would mislead. */}
            </div>

            {/* Row 3: SL/TP bar — levels from the current bracket leg rows (R7), entry-row copy
                only as a no-leg-rows fallback (see slLevel/tpLevel above) */}
            {(slLevel || tpLevel) && (
              <SLTPProgressBar
                sl={slLevel}
                tp={tpLevel}
                entry={trade.entryPrice}
                ltp={ltp}
                side={trade.side}
              />
            )}

            {/* Expand hint */}
            <div style={{ textAlign: 'center', marginTop: 8, color: 'var(--fg-3)', fontSize: 11 }}>
              {expanded ? '▲ Hide orders' : '▼ View orders'}
            </div>
          </>
        ) : (
          /* ---- CLOSED TRADE HEADER ---- */
          <>
            {/* Row 1: instrument + #num + side + entry→exit + P&L */}
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 10, marginBottom: 6 }}>
              <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap', minWidth: 0 }}>
                {instrumentTag}
                {/* BD groups have no sequential trade number (tradeNumber is null) — guard
                    against rendering a bare "#" for paired/converted BD trades. */}
                {trade.tradeNumber != null && (
                  <span style={{ color: 'var(--fg-3)', fontSize: 11, fontWeight: 500 }}>#{trade.tradeNumber}</span>
                )}
                {/* BD FIFO-pairing (Phase 1): paired closed round trips (and conversions)
                    render through this header — carry the same provenance badge. */}
                {isBrokerDirect && <Badge variant="warn" size="sm">Broker-direct</Badge>}
                <span style={{ color: sideColor, fontWeight: 700, fontSize: 13 }}>{trade.side}</span>
                <span style={{ color: 'var(--fg-1)', fontSize: 13 }}>{_fmtPrice(trade.entryPrice)}</span>
                <span style={{ color: 'var(--fg-3)' }}>&rarr;</span>
                <span style={{ color: 'var(--fg-1)', fontSize: 13 }}>{_fmtPrice(trade.exitPrice)}</span>
              </div>
              {trade.lifecycleNet && !entryOutcome ? (
                <ClosedNetHeadline lifecycleNet={trade.lifecycleNet} />
              ) : (
                <span style={{ color: pnlColor, fontWeight: 700, fontSize: 15, whiteSpace: 'nowrap' }}>
                  {trade.pnl !== null ? (trade.pnl >= 0 ? '+' : '') + trade.pnl.toFixed(2) : '—'}
                </span>
              )}
            </div>

            {/* Row 2: exit-reason badge + time→time + duration + qty + expand */}
            <div style={{ display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap' }}>
              {entryOutcome ? (
                <Badge variant="warn" size="sm">
                  {entryOutcome}
                </Badge>
              ) : trade.exitReason && (
                <Badge variant={EXIT_VARIANT[trade.exitReason] || 'neutral'} size="sm">
                  {trade.exitReason}
                </Badge>
              )}
              <span style={metaStyle}>
                {trade.entryTime ? _fmtTimeIST(new Date(trade.entryTime).toISOString()) : '—'}
                {' → '}
                {trade.exitTime ? _fmtTimeIST(new Date(trade.exitTime).toISOString()) : '—'}
              </span>
              <span style={metaStyle}>{trade.duration || '—'}</span>
              <span style={metaStyle}>Qty {trade.qty}</span>
              <span style={{ marginLeft: 'auto', color: expanded ? 'var(--color-accent)' : 'var(--fg-3)', fontSize: 11 }}>
                {expanded ? '▲ Hide orders' : '▼ View orders'}
              </span>
            </div>
          </>
        )}
      </div>

      {/* ---- Expanded: Mini Order Table ---- */}
      <div style={{
        maxHeight: expanded ? contentHeight : 0,
        overflow: 'hidden',
        transition: 'max-height 200ms ease-out',
      }}>
        <div ref={contentRef} style={{ borderTop: '1px solid var(--divider)', background: 'var(--bg-3)' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
            <thead>
              <tr style={{ color: 'var(--fg-3)', textTransform: 'uppercase', fontSize: 10, letterSpacing: '0.5px' }}>
                {/* #10472 (R7) — Price column (current working price, getOrderWorkingPrice) inserted
                    before Filled Price so the leg table shows what the leg is resting at, not just
                    what it filled at. */}
                {/* 144-6 R7 — Charges: per-order charges from the one authority (number or reason). */}
                {['Leg', 'Side', 'Type', 'Qty', 'Price', 'Filled Price', 'Charges', 'Status', 'Time'].map((h, i) => (
                  <th key={h} style={{
                    padding: '7px 12px', textAlign: i >= 3 ? 'right' : 'left',
                    borderBottom: '1px solid var(--divider)', fontWeight: 600,
                    ...(h === 'Status' ? { textAlign: 'center' } : {}),
                  }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {allOrders.map((order, legIdx) => {
                const isCancelled = order.status === 'CANCELLED';
                //--> Derive leg type: prefer the specific exit_reason (sl/tp/time_exit) over
                //*  the generic leg_type (rule_exit groups all signal-driven exits). Falls back
                //*  to leg_type for bracket children (which have no exit_reason) and entries.
                //! Broker-direct legs carry none of exit_reason/leg_type/action, so the final
                //!  fallback keys off POSITION in allOrders (=[entryOrder, ...bracketOrders]):
                //!  index 0 is the entry, every subsequent leg is a closing (exit) leg.
                const legType = order.exit_reason || order.leg_type || order.action
                  || (legIdx === 0 ? 'entry' : 'exit');
                //--> 10509-12 E13 / R10 — a converted SL names the true exit and its origin.
                const legDisplay = formatExitConversion(order) || LEG_TYPE_DISPLAY[legType] || legType;
                const statusColor = isCancelled ? 'var(--fg-2)'
                  : order.status === 'FILLED' ? 'var(--color-accent)'
                  : 'var(--warning)';

                return (
                  <tr key={order.id || order.order_id} style={{
                    color: isCancelled ? 'var(--fg-2)' : 'var(--fg-1)',
                    opacity: isCancelled ? 0.5 : 1,
                  }}>
                    <td style={{ padding: '7px 12px', borderBottom: '1px solid var(--divider)', fontWeight: 600 }}>
                      {legDisplay}
                    </td>
                    <td style={{
                      padding: '7px 12px', borderBottom: '1px solid var(--divider)',
                      color: order.side === 'BUY' ? 'var(--color-profit)' : 'var(--color-loss)',
                    }}>{order.side}</td>
                    <td style={{ padding: '7px 12px', borderBottom: '1px solid var(--divider)', color: 'var(--fg-2)' }}>
                      {order.order_type}
                    </td>
                    <td style={{ padding: '7px 12px', borderBottom: '1px solid var(--divider)', textAlign: 'right' }}>
                      {order.qty}
                    </td>
                    <td style={{ padding: '7px 12px', borderBottom: '1px solid var(--divider)', textAlign: 'right' }}>
                      {_fmtPrice(getOrderWorkingPrice(order))}
                    </td>
                    <td style={{ padding: '7px 12px', borderBottom: '1px solid var(--divider)', textAlign: 'right' }}>
                      {_fmtPrice(order.filled_price)}
                    </td>
                    <td data-testid="leg-charges" style={{ padding: '7px 12px', borderBottom: '1px solid var(--divider)', textAlign: 'right' }}>
                      <OrderChargesCell order={order} />
                    </td>
                    <td style={{ padding: '7px 12px', borderBottom: '1px solid var(--divider)', textAlign: 'center' }}>
                      <span style={{
                        background: isCancelled ? 'var(--bg-3)' : 'color-mix(in srgb, var(--primary) 15%, transparent)',
                        color: statusColor,
                        padding: '2px 8px', borderRadius: 4, fontSize: 11,
                      }}>{order.status}</span>
                      {/* --> Reject reason label: only shown when REJECTED and reason is present */}
                      {order.status === 'REJECTED' && rejectReasonLabel(fullRejectReason(order)) && (
                        <span style={{ display: 'block', color: 'var(--fg-2)', fontSize: '0.7rem', marginTop: 3 }}>
                          {rejectReasonLabel(fullRejectReason(order))}
                        </span>
                      )}
                    </td>
                    <td style={{ padding: '7px 12px', borderBottom: '1px solid var(--divider)', textAlign: 'right', color: 'var(--fg-2)' }}>
                      {(() => {
                        //--> WS events carry `timestamp`, REST carries `created_at`. Either is fine.
                        const ts = order.created_at || order.timestamp || order._epoch;
                        return ts ? _fmtTimeIST(ts) : '—';
                      })()}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {/* Keyframe for LIVE pulse — respects prefers-reduced-motion (H1) */}
      <style>{`
        @media (prefers-reduced-motion: no-preference) {
          @keyframes tradePulse { 0%,100%{opacity:1} 50%{opacity:0.6} }
        }
        @media (prefers-reduced-motion: reduce) {
          @keyframes tradePulse { 0%,100%{opacity:1} }
        }
      `}</style>
    </div>
  );
}
